import type { IconProps } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  HandTap as HandTapIcon,
  CursorClick as CursorClickIcon,
  SealCheck as SealCheckIcon,
  Warning as WarningIcon,
  Bell as BellIcon,
  GearSix as GearSixIcon,
  MagnifyingGlass as MagnifyingGlassIcon,
  Tag as TagIcon,
  ArrowUUpLeft as ArrowUUpLeftIcon,
  Invoice as InvoiceIcon,
  User as UserIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { Agent, Task } from './data';

export function relTime(ts: number): string {
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

export function clockTime(ts: number): string {
  const d = new Date(ts);
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
}

const AGENT_ICONS: Record<string, React.ComponentType<IconProps>> = {
  MagnifyingGlass: MagnifyingGlassIcon,
  Tag: TagIcon,
  ArrowUUpLeft: ArrowUUpLeftIcon,
  CursorClick: CursorClickIcon,
  Invoice: InvoiceIcon,
};

export function AgentAvatar({
  agent,
  size = 26,
}: {
  agent: Agent | undefined;
  size?: number;
}) {
  if (!agent) {
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded-md bg-stone-200 text-stone-600"
        style={{ width: size, height: size }}
      >
        <GearSixIcon className="size-[60%]" />
      </div>
    );
  }
  const Icon = AGENT_ICONS[agent.icon] || GearSixIcon;
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-md text-white"
      style={{ width: size, height: size, background: agent.tint }}
    >
      <Icon className="size-[58%]" />
    </div>
  );
}

export function PersonAvatar({ size = 26 }: { size?: number }) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-md bg-stone-300 text-stone-700"
      style={{ width: size, height: size }}
    >
      <UserIcon className="size-[58%]" weight="fill" />
    </div>
  );
}

/**
 * Renders a task's *true* owner, not the agent it merely displays under.
 * - Agent genuinely owns it (in the agent's queue) → agent avatar + name.
 * - Escalated to / assigned to a person → person avatar + name, plus a
 *   "↩ <Agent>" breadcrumb of where it came from so the origin isn't lost.
 * - Neither → "Unassigned" (with the from-agent breadcrumb when known).
 *
 * This is the fix for the inbox conflating "owned by agent" with "escalated
 * from agent": only `isAgentAssigned` tasks read as the agent's.
 */
export function TaskAssignee({
  task,
  agent,
  assigneeUserName,
  size = 14,
  showFrom = true,
}: {
  task: Task;
  agent: Agent | undefined;
  assigneeUserName?: string;
  size?: number;
  showFrom?: boolean;
}) {
  const gt = useGT();
  const fromAgent = showFrom && agent ? agent.name : null;

  // Origin breadcrumb — shown when a task displays under an agent it was
  // escalated *from* but no longer belongs to.
  const fromBadge = fromAgent ? (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 text-stone-400"
      title={gt('Escalated from {agent}', { agent: fromAgent })}
    >
      <ArrowUUpLeftIcon className="size-3" />
      {fromAgent}
    </span>
  ) : null;

  // Agent-owned tasks must never read as "Unassigned" — even when the agent
  // lookup misses (it's permission-gated, so `agent` can be undefined here).
  if (task.isAgentAssigned) {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <AgentAvatar agent={agent} size={size} />
        <span className="truncate">{agent?.name ?? <T>Assigned agent</T>}</span>
      </span>
    );
  }

  if (task.assigneeUserId) {
    return (
      <span className="flex min-w-0 items-center gap-2">
        <PersonAvatar size={size} />
        <span className="truncate">{assigneeUserName ?? <T>Assigned</T>}</span>
        {fromBadge}
      </span>
    );
  }

  return (
    <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
      <PersonAvatar size={size} />
      <span className="truncate">
        <T>Unassigned</T>
      </span>
      {fromBadge}
    </span>
  );
}

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    classes: 'bg-stone-100 text-stone-700 border-stone-300',
    dotClass: 'bg-stone-500',
    animate: false,
  },
  in_progress: {
    label: 'In progress',
    classes:
      'bg-[oklch(0.96_0.03_240)] text-[oklch(0.42_0.13_250)] border-[oklch(0.86_0.07_240)]',
    dotClass: 'bg-[oklch(0.62_0.14_250)]',
    animate: true,
    animationDuration: '1.6s',
  },
  completed: {
    label: 'Done',
    classes:
      'bg-[oklch(0.96_0.04_150)] text-[oklch(0.4_0.1_150)] border-[oklch(0.82_0.1_150)]',
    dotClass: 'bg-[oklch(0.6_0.14_150)]',
    animate: false,
  },
  failed: {
    label: 'Failed',
    classes:
      'bg-[oklch(0.96_0.04_25)] text-[oklch(0.44_0.16_25)] border-[oklch(0.84_0.1_25)]',
    dotClass: 'bg-[oklch(0.62_0.18_25)]',
    animate: false,
  },
  cancelled: {
    label: 'Cancelled',
    classes: 'bg-stone-100 text-stone-500 border-stone-200',
    dotClass: 'bg-stone-400',
    animate: false,
  },
  needs_input: {
    label: 'Needs input',
    classes:
      'bg-[oklch(0.96_0.06_80)] text-[oklch(0.42_0.14_60)] border-[oklch(0.84_0.1_80)]',
    dotClass: 'bg-[oklch(0.7_0.14_70)]',
    animate: true,
    animationDuration: '2s',
  },
} as const;

export function StatusPill({ status }: { status: Task['status'] }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium leading-snug',
        c.classes,
      )}
    >
      <span
        className={cn('size-1.5 rounded-full', c.dotClass)}
        style={
          c.animate
            ? {
                animation: `pulse ${c.animationDuration} ease-in-out infinite`,
              }
            : undefined
        }
      />
      {c.label}
    </span>
  );
}

const KIND_CONFIG = {
  hitl: {
    label: 'Needs you',
    classes: 'bg-[oklch(0.96_0.06_80)] text-[oklch(0.42_0.14_60)]',
    Icon: HandTapIcon,
  },
  'cu-review': {
    label: 'Review action',
    classes: 'bg-[oklch(0.95_0.05_290)] text-[oklch(0.4_0.16_290)]',
    Icon: CursorClickIcon,
  },
  approval: {
    label: 'Approval Required',
    classes: 'bg-[oklch(0.96_0.04_240)] text-[oklch(0.4_0.14_250)]',
    Icon: SealCheckIcon,
  },
  'workflow-fail': {
    label: 'Failed',
    classes: 'bg-[oklch(0.96_0.04_25)] text-[oklch(0.44_0.16_25)]',
    Icon: WarningIcon,
  },
  system: {
    label: 'System',
    classes: 'bg-stone-100 text-stone-700',
    Icon: BellIcon,
  },
} as const;

export function KindChip({ kind }: { kind: string }) {
  const c = KIND_CONFIG[kind as keyof typeof KIND_CONFIG] || KIND_CONFIG.system;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-px text-[10.5px] font-semibold leading-snug',
        c.classes,
      )}
    >
      <c.Icon className="size-[11px]" />
      {c.label}
    </span>
  );
}

export function PriorityDot({
  priority = 'medium',
}: {
  priority?: Task['priority'];
}) {
  const colorClass =
    priority === 'urgent' || priority === 'high'
      ? 'bg-[oklch(0.62_0.2_25)]'
      : priority === 'low'
        ? 'bg-stone-300'
        : 'bg-stone-500';
  return (
    <span
      className={cn('inline-block size-[7px] rounded-full', colorClass)}
      title={`${priority.charAt(0).toUpperCase() + priority.slice(1)} priority`}
    />
  );
}

export function MetaRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-2.5 py-1 text-xs">
      <span className="w-[88px] text-[11px] text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 flex-1 break-words text-stone-800">
        {children}
      </span>
    </div>
  );
}

export function ListSectionHeader({
  label,
  count,
  accent,
}: {
  label: string;
  count?: number;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        'sticky top-0 z-[1] flex items-center gap-2 border-t border-border px-3 pb-1.5 pt-2.5',
        accent
          ? 'bg-gradient-to-b from-[oklch(0.985_0.018_75)] to-transparent'
          : 'bg-transparent',
      )}
    >
      <span
        className={cn(
          'text-[10px] font-bold uppercase tracking-[0.07em]',
          accent ? 'text-[oklch(0.42_0.14_60)]' : 'text-stone-500',
        )}
      >
        {label}
      </span>
      {count != null && (
        <span
          className={cn(
            'rounded-full px-1.5 py-px text-[10px] font-semibold',
            accent
              ? 'bg-[oklch(0.92_0.07_70)] text-[oklch(0.42_0.14_60)]'
              : 'bg-stone-200 text-stone-500',
          )}
        >
          {count}
        </span>
      )}
    </div>
  );
}

export function EmptyState({
  icon: IconComponent,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center p-8 text-center text-muted-foreground">
      <div className="mb-3 flex size-12 items-center justify-center rounded-xl bg-stone-100">
        <IconComponent className="size-[22px] text-stone-500" />
      </div>
      <div className="mb-1 text-[13px] font-medium text-stone-800">{title}</div>
      <div className="max-w-[280px] text-xs leading-normal">{children}</div>
    </div>
  );
}

// NOTE: param _agents currently unused (kept via '_' prefix) — revisit and delete if not needed
export function CompactTaskRow({
  task,
  first,
  agents: _agents,
}: {
  task: Task;
  first: boolean;
  agents: Record<string, Agent>;
}) {
  const hasBlocker = task.blockedOn != null;
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2.5',
        !first && 'border-t border-border',
        hasBlocker ? 'bg-[oklch(0.99_0.012_80)]' : 'bg-white',
      )}
    >
      <PriorityDot priority={task.priority} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium text-stone-900">
          {task.title}
        </div>
        {task.blockedOn && (
          <div className="mt-0.5 text-[11px] text-[oklch(0.46_0.13_70)]">
            <HandTapIcon className="mb-px mr-1 inline size-[11px]" />
            {task.blockedOn}
          </div>
        )}
        {task.error && (
          <div className="mt-0.5 text-[11px] text-[oklch(0.5_0.16_25)]">
            <WarningIcon className="mb-px mr-1 inline size-[11px]" />
            {task.error}
          </div>
        )}
      </div>
      <StatusPill status={task.status} />
      <span className="w-[50px] text-right text-[11px] text-muted-foreground">
        {relTime(task.updated)}
      </span>
    </div>
  );
}
